export const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8787";

export type CardMeta = {
  key: string;
  label: string;
  description: string;
};

export const FALLBACK_CATALOG: CardMeta[] = [
  { key: "profile", label: "Profile Card", description: "Avatar, badges and role/Nitro border." },
  { key: "welcome", label: "Welcome Card", description: "Welcome card shown when a member joins the server." },
  { key: "leave", label: "Leave Card", description: "Card shown when a member leaves the server." },
  { key: "levelup", label: "Level Up Card", description: "Level-up notification card." },
  { key: "achievement", label: "Achievement Card", description: "Unlocked achievement, with rarity." },
  { key: "ship", label: "Ship Card", description: "Compatibility between two users." },
  { key: "leaderboard", label: "Leaderboard Card", description: "Leaderboard table (single-entry demo)." },
  { key: "birthday", label: "Birthday Card", description: "Balloons, confetti and an optional age/years-in-server counter." },
  { key: "marriage", label: "Marriage Card", description: "Certificate-style card for two users, with days together." },
  { key: "weeklyrecap", label: "Weekly Recap Card", description: "Personal weekly stats with a 7-day activity chart." },
];

export const DISCORD_ID_PATTERN = /^\d{17,20}$/;

export function isValidDiscordId(value: string): boolean {
  return DISCORD_ID_PATTERN.test(value.trim());
}

export async function fetchCatalog(): Promise<CardMeta[]> {
  const res = await fetch(`${API_BASE}/api/cards`);
  if (!res.ok) throw new Error(`GET /api/cards -> ${res.status}`);
  return res.json();
}

export type PreviewParams = {
  userId: string;
  guildId?: string;
  partnerId?: string;
  guildName?: string;
  customBackground?: string;
  borderColor?: string | string[];
  removeBorder?: boolean;
  extra?: Record<string, string>;
};

export async function fetchCardPreview(
  card: string,
  params: PreviewParams,
): Promise<{ ok: true; url: string } | { ok: false; error: string }> {
  const query = new URLSearchParams();
  if (params.guildId) query.set("guildId", params.guildId);
  if (params.partnerId) query.set("partnerId", params.partnerId);
  if (params.guildName) query.set("guildName", params.guildName);
  if (params.customBackground) query.set("customBackground", params.customBackground);
  if (params.borderColor) {
    if (Array.isArray(params.borderColor)) {
      for (const color of params.borderColor) {
        if (color) query.append("borderColor", color);
      }
    } else {
      query.set("borderColor", params.borderColor);
    }
  }
  if (params.removeBorder) query.set("removeBorder", "true");
  if (params.extra) {
    for (const [key, value] of Object.entries(params.extra)) {
      if (value) query.set(key, value);
    }
  }

  const url = `${API_BASE}/api/preview/${params.userId}/${card}?${query.toString()}`;

  const res = await fetch(url);

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
    }
    return { ok: false, error: message };
  }

  const blob = await res.blob();
  return { ok: true, url: URL.createObjectURL(blob) };
}